import { usePlayer, usePlayers } from "@empirica/core/player/classic/react";
import React, { useState, useEffect, useRef } from "react";

// ─── Task constants ─────────────────────────────────────────────────
const START_POSITION = 300; // TODO: randomize if needed
const FIXED_OBJECT_1_POSITION = 150;
const FIXED_OBJECT_2_POSITION = 450;
const SHADOW_OFFSET = 100;
const SENSOR_RANGE = 15; // the width of the sigmoid sensor function
const SPACE_SIZE = 600;
const HALF_SPACE = SPACE_SIZE / 2;
const MAX_STEP = 2; // max pixels per mouse event
const SPEED_MULTIPLIER = 1; // scale mouse movement

// ─── Sampling config ────────────────────────────────────────────────
const SAMPLE_INTERVAL = 10; // ms between samples
const BATCH_SIZE = 100; // samples per batch (~1 second)

export function PerceptualCrossing({ tutorial = false, stageType = null, hideVisuals = false }) {
  const player = usePlayer();
  const players = usePlayers();

  const [position, setPosition] = useState(START_POSITION);
  const [sensorValue, setSensorValue] = useState(0);
  const [tutorialObjectPosition, setTutorialObjectPosition] = useState(400);

  const containerRef = useRef(null);
  const isLocked = useRef(false);
  const lastPosition = useRef(START_POSITION);

  // Track the current stage safely across transitions
  const stageRef = useRef(null);

  // ─── Sampling & batching refs ────────────────────────────────────────────────
  // Keep track of the latest position/sensor so the sampler can read them
  const latestPositionRef = useRef(position);
  const latestSensorRef = useRef(sensorValue);
  const latestMotorRef = useRef(0);

  // Accumulate samples until we have BATCH_SIZE, then flush
  const samplesRef = useRef([]);
  const batchIndexRef = useRef(0);

  // ─── Sync latest refs any time position or sensorValue changes ─────────────
  useEffect(() => {
    latestPositionRef.current = position;
    latestSensorRef.current = sensorValue;
  }, [position, sensorValue]);

  useEffect(() => {
    stageRef.current = player?.stage ?? null;
  }, [player?.stage]);

  // ─── 10 ms sampling loop + batching and then writes to player.stage.set ─────────────
  useEffect(() => {
    if (!player) return;

    const interval = setInterval(() => {
      const t = Date.now();
      const sample = {
        t,
        x: latestPositionRef.current,
        sensor: latestSensorRef.current,
        motor: latestMotorRef.current,
      };

      samplesRef.current.push(sample);

      if (samplesRef.current.length >= BATCH_SIZE) {
        const key = `movementBatch_${batchIndexRef.current}`;
        stageRef.current?.set(key, samplesRef.current);
        batchIndexRef.current += 1;
        samplesRef.current = [];
      }
    }, SAMPLE_INTERVAL);

    return () => {
      // On unmount (i.e., when stage ends), flush any leftover samples
      if (samplesRef.current.length > 0) {
        const key = `movementBatch_${batchIndexRef.current}`;
        stageRef.current?.set(key, samplesRef.current);
        samplesRef.current = [];
      }
      clearInterval(interval);
    };
  }, [player]); // run once on mount, cleanup on unmount

  // ─── Helpers ─────────────────────────────────────────────────────────────────
  const calculateShadowPosition = (agentPosition) => {
    if (agentPosition === 999999999) return 999999999;
    let shadowPos = agentPosition + SHADOW_OFFSET;
    if (shadowPos >= SPACE_SIZE) shadowPos -= SPACE_SIZE;
    if (shadowPos < 0) shadowPos = SPACE_SIZE + shadowPos;
    return shadowPos;
  };

  const getPeriodicDistance = (pos1, pos2) => {
    if (pos1 === 999999999 || pos2 === 999999999) return 999999999;
    let dist = Math.abs(pos1 - pos2);
    if (dist > HALF_SPACE) dist = SPACE_SIZE - dist;
    return dist;
  };

  const calculateSensor = (
    myPos,
    otherPlayerPos,
    shadowPos,
    sensorRange,
    fixedObjectPos
  ) => {
    const distToOther = getPeriodicDistance(myPos, otherPlayerPos);
    const distToShadow = getPeriodicDistance(myPos, shadowPos);
    const distToFixed = getPeriodicDistance(myPos, fixedObjectPos);
    const mindist = Math.min(distToOther, distToShadow, distToFixed);
    return 1 / (1 + Math.exp((8 * sensorRange) * (mindist - sensorRange)));
  };

  // ─── Move tutorial object if in tutorial mode ────────────────────────────────
  useEffect(() => {
    if (!tutorial) return;
    let interval;
    if (stageType === "slow" || stageType === "fast") {
      const speed = stageType === "slow" ? 1 : 3; // pixels/frame
      interval = setInterval(() => {
        setTutorialObjectPosition((prev) => (prev + speed) % SPACE_SIZE);
      }, 16); // ~60 fps
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [tutorial, stageType]);

  // ─── Pointer‐lock setup ───────────────────────────────────────────────────────
  useEffect(() => {
    const handleClick = () => {
      if (!isLocked.current && containerRef.current) {
        containerRef.current.requestPointerLock();
      }
    };
    const handleLockChange = () => {
      isLocked.current = document.pointerLockElement === containerRef.current;
    };

    document.addEventListener("pointerlockchange", handleLockChange);
    if (containerRef.current) {
      containerRef.current.addEventListener("click", handleClick);
    }
    return () => {
      document.removeEventListener("pointerlockchange", handleLockChange);
      if (containerRef.current) {
        containerRef.current.removeEventListener("click", handleClick);
      }
      document.exitPointerLock();
    };
  }, []);

  // ─── Update own position on mousemove (pointer‐locked) ────────────────────────
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!containerRef.current || !isLocked.current) return;
      
      // For recording mouse "motor" output
      const rawMovement = e.movementX;
      player.set("motorOutput", rawMovement)
      latestMotorRef.current = rawMovement;

      // Bounding velocity
      const containerBounds = containerRef.current.getBoundingClientRect();
      const containerWidth = containerBounds.width;
      const movement = e.movementX;
      const scaledMovement = movement * SPEED_MULTIPLIER;
      const limitedMovement =
        Math.sign(scaledMovement) * Math.min(Math.abs(scaledMovement), MAX_STEP);
      let newPos = lastPosition.current + limitedMovement;

      // Periodic boundary
      if (newPos < 0) {
        newPos = containerWidth + (newPos % containerWidth);
      } else {
        newPos = newPos % containerWidth;
      }

      setPosition(newPos);
      player.set("position", newPos);
      lastPosition.current = newPos;
    };

    window.addEventListener("mousemove", handleMouseMove);
    document.body.style.cursor = "none"; // hide cursor
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      document.body.style.cursor = "default"; // restore cursor
    };
  }, [player]);

  // ─── Update sensorValue whenever position, players, or tutorial changes ───────
  useEffect(() => {
    const otherPlayer = players.find((p) => p.id !== player.id);
    const otherPosition = otherPlayer?.get("position") ?? 999999999;
    const shadowOfOtherPlayer = calculateShadowPosition(otherPosition);
    const isFirstPlayer = player.id === players[0]?.id;
    const myFixedObjectPos = isFirstPlayer
      ? FIXED_OBJECT_2_POSITION
      : FIXED_OBJECT_1_POSITION;

    if (tutorial) {
      const distTutorial = getPeriodicDistance(position, tutorialObjectPosition);
      const sensor = 1 / (1 + Math.exp((8 * SENSOR_RANGE) * (distTutorial - SENSOR_RANGE)));
      setSensorValue(sensor);
      return;
    }

    const sensor = calculateSensor(
      position,
      otherPosition,
      shadowOfOtherPlayer,
      SENSOR_RANGE,
      myFixedObjectPos
    );
    setSensorValue(sensor);
  }, [position, players, player.id, tutorial, tutorialObjectPosition]);

  // ─── Final render ─────────────────────────────────────────────────────────────
  const clampedSensor = Math.max(0, Math.min(1, sensorValue));

  if (hideVisuals) {
    return (
      <div className="h-screen w-screen flex items-center justify-center relative">
        {/* ESC key reminder */}
        <div className="fixed top-4 right-4 bg-gray-100 rounded-lg p-2 shadow-md text-sm text-gray-600">
          Press ESC to release mouse control
        </div>

        {/* Invisible interaction area */}
        <div
          ref={containerRef}
          className="absolute inset-0 cursor-none"
          style={{ touchAction: "none" }}
        />

        {/* Large centered sensor circle */}
        <div
          className="w-96 h-96 bg-green-500 rounded-full transition-opacity duration-100"
          style={{ opacity: sensorValue }}
        />
      </div>
    );
  }

  return (
    // <div className="h-full w-full flex items-center justify-center">
      <div className="flex flex-col items-center space-y-10">
      {/* ESC key reminder */}
      <div className="fixed top-4 right-4 bg-gray-100 rounded-lg p-2 shadow-md text-sm text-gray-600">
        Press ESC to release mouse control
      </div>

      <div className="text-lg">
        {!isLocked.current
          ? "Double click on the gray box to start, then move your mouse left and right to navigate"
          : "Move your mouse left and right to navigate the environment"}
      </div>

      <div
        ref={containerRef}
        className="relative bg-gray-200 cursor-pointer"
        style={{ width: `${SPACE_SIZE}px`, height: "80px" }}
      >
        {/* Player position indicator (lights up with sensor) */}
        <div
          className="absolute top-0 w-2 h-full"
          style={{
            left: `${position}px`,
            backgroundColor: `rgb(${Math.round(59 + (34 - 59) * clampedSensor)}, ${Math.round(
              130 + (197 - 130) * clampedSensor
            )}, ${Math.round(246 + (94 - 246) * clampedSensor)})`,
          }}
        />

        {/* Tutorial object */}
        {tutorial && (
          <div
            className="absolute top-0 w-2 h-full bg-red-500"
            style={{ left: `${tutorialObjectPosition}px` }}
          />
        )}

      </div>
      <div className="text-sm text-gray-500">
        The avatar color indicates proximity to other elements
      </div>

        {/* Debug display */}
        {tutorial && (
          <div className="text-xs text-gray-400">
            Sensor value: {sensorValue.toFixed(3)}
          </div>
        )}
      </div>
    // </div>
  );
}